import { useState, useCallback } from 'react';
import { Play, Pause, RotateCcw, Timer as TimerIcon } from 'lucide-react';
import { useCountdown } from '../hooks/useCountdown';

// Phase 8 (Exam Prep orchestration port): a standalone, reusable Pomodoro-
// style timer. Distinct from PracticeTestTaking's exam-pacing timer (different
// product purpose — general focus/break pacing while a student works through
// Practice/Revision/Cheat Sheet, not tied to any one question).

const FOCUS_MS = 25 * 60 * 1000;
const BREAK_MS = 5 * 60 * 1000;

function formatRemaining(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function PomodoroTimer() {
  const [mode, setMode] = useState<'focus' | 'break'>('focus');

  const handleComplete = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'focus' ? 'break' : 'focus';
      countdown.start(next === 'focus' ? FOCUS_MS : BREAK_MS);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countdown = useCountdown(handleComplete);

  function handleStartPause() {
    if (countdown.isRunning) {
      countdown.pause();
    } else if (countdown.remainingMs > 0) {
      countdown.start(countdown.remainingMs);
    } else {
      countdown.start(mode === 'focus' ? FOCUS_MS : BREAK_MS);
    }
  }

  function handleReset() {
    countdown.reset();
    setMode('focus');
  }

  const totalMs = mode === 'focus' ? FOCUS_MS : BREAK_MS;
  const progress = countdown.remainingMs > 0 ? 1 - countdown.remainingMs / totalMs : 0;

  return (
    <div className="glass-card rounded-xl p-4 shadow-sm flex items-center gap-4">
      <div
        className="relative w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          background: `conic-gradient(${mode === 'focus' ? '#6366f1' : '#22c55e'} ${progress * 360}deg, #e5e7eb 0deg)`,
        }}
      >
        <div className="absolute inset-1 bg-white rounded-full flex items-center justify-center">
          <TimerIcon className="w-5 h-5 text-gray-400" />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {mode === 'focus' ? 'Focus' : 'Break'}
        </p>
        <p className="text-2xl font-bold text-ocean-deep font-mono">
          {formatRemaining(countdown.remainingMs || totalMs)}
        </p>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleStartPause}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-coral-warm hover:bg-orange-600 text-white transition-colors"
          aria-label={countdown.isRunning ? 'Pause' : 'Start'}
        >
          {countdown.isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
        </button>
        <button
          onClick={handleReset}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
          aria-label="Reset"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
